import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Image } from '~/eds/src/components/Image/Image';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { Chart } from '~/types/DashboardV2';

interface EmptyChartProps {
  errorCode: number | undefined;
  chart: Chart;
  isDataOverload?: boolean;
  isError: boolean;
  width: number;
}

export const EmptyChart = ({
  errorCode,
  chart,
  isDataOverload,
  isError,
  width,
}: EmptyChartProps) => {
  const intl = useIntl();

  const getImage = useMemo(() => {
    if (isError) {
      return (
        <Image
          alt={intl.formatMessage({
            id: 'EmptyChart.errorLoadingChartAlt',
            defaultMessage: 'Error Loading Chart',
            description: 'Alt text for error loading chart image',
          })}
          image="chart-error"
        />
      );
    }

    if (isDataOverload) {
      return (
        <Image
          alt={intl.formatMessage({
            id: 'EmptyChart.errorLoadingChartAlt',
            defaultMessage: 'Error Loading Chart',
            description: 'Alt text for error loading chart image',
          })}
          image="chart-too-much-data"
        />
      );
    }

    return (
      <Image
        alt={intl.formatMessage({
          id: 'EmptyChart.emptyChartAlt',
          defaultMessage: 'Empty Chart',
          description: 'Alt text for empty chart image',
        })}
        image="chart-empty"
      />
    );
  }, [isError, isDataOverload]);

  const getContent = useMemo(() => {
    if (isError) {
      if (errorCode === HttpStatusCodeType.TooManyRequests) {
        return (
          <Text mb={1} textAlign="center">
            <FormattedMessage
              id="EmptyChart.errorLoadingChart"
              defaultMessage="There was an error while loading this chart."
              description="Error message when chart fails to load"
            />
          </Text>
        );
      } else if (errorCode === HttpStatusCodeType.UnprocessableContent) {
        return (
          <>
            <Text mb={1} textAlign="center">
              <FormattedMessage
                id="EmptyChart.noDataAvailable"
                defaultMessage="There's no <strong>{chartLabel}</strong> data available."
                description="Message when no data is available for the chart. Example: chartLabel = 'Contract Volume'"
                values={{
                  strong: (chunks) => <Text variant="body-bold">{chunks}</Text>,
                  chartLabel: chart.label,
                }}
              />
            </Text>
            <Text textAlign="center">
              <FormattedMessage
                id="EmptyChart.selectDifferentChart"
                defaultMessage="Please select a different chart."
                description="Instruction to select a different chart"
              />
            </Text>
          </>
        );
      }
      return (
        <>
          <Text mb={1} textAlign="center">
            <FormattedMessage
              id="EmptyChart.cannotLoadChart"
              defaultMessage="We can't load this chart right now."
              description="Error message when chart cannot be loaded"
            />
          </Text>
          <Text textAlign="center">
            <FormattedMessage
              id="EmptyChart.refreshDashboard"
              defaultMessage="Please refresh to reload this dashboard and try to update."
              description="Instruction to refresh dashboard"
            />
          </Text>
        </>
      );
    } else if (isDataOverload) {
      return (
        <>
          <Text mb={1} textAlign="center">
            <FormattedMessage
              id="EmptyChart.tooMuchData"
              defaultMessage="There's too much <strong>{chartLabel}</strong> data to display."
              description="Message when there's too much data to display. Example: chartLabel = 'Contract Volume'"
              values={{
                strong: (chunks) => <Text variant="body-bold">{chunks}</Text>,
                chartLabel: chart.label,
              }}
            />
          </Text>
          <Text textAlign="center">
            <FormattedMessage
              id="EmptyChart.selectSmallerRange"
              defaultMessage="Please select a smaller date range or a different grouping."
              description="Instruction to select smaller date range"
            />
          </Text>
        </>
      );
    }
    return (
      <>
        <Text mb={1} textAlign="center">
          <FormattedMessage
            id="EmptyChart.noMatchingData"
            defaultMessage="There's no <strong>{chartLabel}</strong> data that matches your search."
            description="Message when no data matches the search. Example: chartLabel = 'Contract Volume'"
            values={{
              strong: (chunks) => <Text variant="body-bold">{chunks}</Text>,
              chartLabel: chart.label,
            }}
          />
        </Text>
        <Text textAlign="center">
          <FormattedMessage
            id="EmptyChart.tryDifferentSearch"
            defaultMessage="You can try a different search or filters."
            description="Instruction to try different search or filters"
          />
        </Text>
      </>
    );
  }, [errorCode, isError, isDataOverload]);

  return (
    <Layout
      w={width}
      h="300px"
      align="center"
      direction="column"
      p={4}
      justify="center"
    >
      {getImage}
      <Layout direction="column" mt={2} p={10}>
        {getContent}
      </Layout>
    </Layout>
  );
};
